// File: frontend/src/pages/Users/UsersPage.jsx
// Complete Users management page for WA Contact Center CRM

import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { getUsers, getRoles } from '../../services/api';
import UsersList from '../../components/Users/UsersList';
import UsersForm from '../../components/Users/UsersForm';
import UserDetail from '../../components/Users/UserDetail';
import UsersStats from '../../components/Users/UsersStats';

const UsersPage = () => {
  const [view, setView] = useState('list'); // 'list', 'create', 'edit', 'detail'
  const [selectedUser, setSelectedUser] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    active: 'true'
  });

  // Fetch users data
  const { 
    data: usersData, 
    isLoading: usersLoading, 
    isError: usersError,
    error: usersErrorDetails,
    refetch: refetchUsers 
  } = useQuery({
    queryKey: ['users', filters],
    queryFn: () => getUsers(filters),
    keepPreviousData: true,
    staleTime: 30000 // 30 seconds
  });

  // Fetch roles for dropdowns
  const { 
    data: rolesData, 
    isLoading: rolesLoading 
  } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
    staleTime: 300000 // 5 minutes
  });

  const users = usersData?.data || [];
  const roles = rolesData?.data || [];

  // Handle view changes
  const handleViewChange = (newView, user = null) => {
    setView(newView);
    setSelectedUser(user);
  };

  // Handle successful operations
  const handleSuccess = (message) => {
    refetchUsers();
    setView('list');
    setSelectedUser(null);
    // You could add toast notification here
    console.log('✅ Success:', message);
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-8">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Gestão de Usuários
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Gerencie usuários, funções e permissões do sistema CRM
            </p>
          </div>
          
          {view === 'list' && (
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <button
                onClick={() => handleViewChange('create')}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Novo Usuário
              </button>
            </div>
          )}
        </div>

        {/* Breadcrumb navigation for non-list views */}
        {view !== 'list' && (
          <nav className="flex mt-4" aria-label="Breadcrumb">
            <ol className="inline-flex items-center space-x-1 md:space-x-3">
              <li>
                <button
                  onClick={() => handleViewChange('list')}
                  className="text-blue-600 hover:text-blue-800"
                >
                  Usuários
                </button>
              </li>
              <li>
                <span className="mx-2 text-gray-400">/</span>
                <span className="text-gray-500 capitalize">
                  {view === 'create' ? 'Criar Usuário' : 
                   view === 'edit' ? 'Editar Usuário' : 
                   view === 'detail' ? 'Detalhes do Usuário' : view}
                </span>
              </li>
            </ol>
          </nav>
        )}
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Users Stats - only show on list view */}
        {view === 'list' && !usersLoading && !usersError && (
          <UsersStats users={users} roles={roles} />
        )}

        {/* Loading State */}
        {usersLoading && (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="text-gray-600">Carregando usuários...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {usersError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <h3 className="text-red-800 font-medium">Erro ao carregar usuários</h3>
            </div>
            <p className="text-red-600 mt-2">
              {usersErrorDetails?.response?.data?.message || usersErrorDetails?.message || 'Erro desconhecido'}
            </p>
            <button
              onClick={() => refetchUsers()}
              className="mt-3 text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {/* Content based on current view */}
        {!usersLoading && !usersError && (
          <>
            {view === 'list' && (
              <UsersList
                users={users}
                roles={roles}
                filters={filters}
                onFilterChange={handleFilterChange}
                onViewUser={(user) => handleViewChange('detail', user)}
                onEditUser={(user) => handleViewChange('edit', user)}
                onCreateUser={() => handleViewChange('create')}
                onRefresh={refetchUsers}
              />
            )}

            {view === 'create' && (
              <UsersForm
                mode="create"
                roles={roles}
                onSuccess={handleSuccess}
                onCancel={() => handleViewChange('list')}
              />
            )}

            {view === 'edit' && selectedUser && (
              <UsersForm
                mode="edit"
                user={selectedUser}
                roles={roles}
                onSuccess={handleSuccess}
                onCancel={() => handleViewChange('list')}
              />
            )}

            {view === 'detail' && selectedUser && (
              <UserDetail
                user={selectedUser}
                roles={roles}
                onEdit={() => handleViewChange('edit', selectedUser)}
                onBack={() => handleViewChange('list')}
                onRefresh={refetchUsers}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UsersPage;